import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, List, Space } from 'antd';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MOBILE } from '@app/constants';
import { useUserPrint, useWindowSize } from '@app/hooks';
import { ScheduleCell } from '@app/pages/WorkSchedule/components/Schedule/ScheduleCell';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NEmployees } from '@app/types';
import { generateKey, shortName } from '@app/utils';

import {
  changeFewSchedules,
  getDatesForFewMonths,
  getDayName,
  getFollowingMonth,
  getPreviousMonth,
  handleMouseOver,
  isWeekend,
  getPeriodForSchedule,
} from '@pages/WorkSchedule/utils';

import { EmployeeContainer } from '@pages/WorkSchedule/components';
import { useScheduleRights, useVirtualScroll } from '@pages/WorkSchedule/hooks';
import type { NWorkSchedule } from '@pages/WorkSchedule/WorkSchedule.types';

import type { CSSProperties } from 'react';

const SCHEDULE_ROW_HEIGHT = 70;
const MONTH_DAYS_ROW_HEIGHT = 82;
const SCHEDULE_ELEMENT_WIDTH = 44;

export const ScheduleContent: NWorkSchedule.Content = ({
  filters,
  selectedPositions,
  selectedEmployees,
  photosAndMiddleNames,
  daysCount,
  spaceInDays,
  selectedDays,
  initialSchedules,
  currentMonth,
  renderedMonths,
  cellsPerRowCount,
  startTime,
  endTime,
  mode,
  handleScroll,
  moveUserToNewMonth,
  cells,
  changingCells,
  deletingCells,
  setChangingCells,
  setDeletingCells,
}) => {
  const { innerWidth = 0 } = useWindowSize() ?? {};
  const { activeBranchId } = useUserPrint();
  const hoveredCells = useRef([]);
  const schedulesContainerRef = useRef<HTMLDivElement>(null);

  const {
    virtualItems = [],
    scrollElementHeight = 0,
    isPageScroll = false,
  } = useVirtualScroll(schedulesContainerRef, selectedEmployees?.length || 0) ??
  {};

  const rights = useScheduleRights();

  const changeFewSchedulesCB = useCallback(
    (employee: NEmployees.ModelEmployee, date: string) => {
      const period = getPeriodForSchedule(
        filters,
        daysCount || '2',
        spaceInDays || '2',
        date,
        selectedDays as NWorkSchedule.SelectedDays,
        renderedMonths
      );
      changeFewSchedules(
        period,
        employee.Id,
        startTime,
        endTime,
        activeBranchId,
        mode,
        cells,
        changingCells,
        deletingCells,
        setChangingCells,
        setDeletingCells,
        {
          ...rights,
        }
      );
    },
    [
      filters,
      daysCount,
      spaceInDays,
      selectedDays,
      renderedMonths,
      startTime,
      endTime,
      activeBranchId,
      mode,
      cells,
      changingCells,
      deletingCells,
      setChangingCells,
      setDeletingCells,
      rights,
    ]
  );

  const hoverSchedules = useCallback(
    (employee: NEmployees.ModelEmployee, date: string) => {
      const period = getPeriodForSchedule(
        filters,
        daysCount || '2',
        spaceInDays || '2',
        date,
        selectedDays as NWorkSchedule.SelectedDays,
        renderedMonths
      );
      handleMouseOver(
        period,
        employee.Id,
        startTime,
        endTime,
        hoveredCells,
        mode,
        {
          ...rights,
        }
      );
    },
    [
      daysCount,
      endTime,
      filters,
      mode,
      renderedMonths,
      rights,
      selectedDays,
      spaceInDays,
      startTime,
    ]
  );

  const pageContent = document.querySelector('.bc-work-schedule');
  const sidebarAndContentContainer = document.querySelector(
    '.sidebar-and-content'
  );

  const [monthStyles, setMonthStyles] = useState<CSSProperties | undefined>({
    top: 'auto',
    position: 'fixed',
  });

  useEffect(() => {
    const onScrollHandler = () => {
      if (sidebarAndContentContainer && innerWidth <= MOBILE) {
        const rect = sidebarAndContentContainer.getBoundingClientRect();
        if (rect.top <= 0) {
          setMonthStyles({
            paddingTop: '10px',
            top: 0,
            position: 'fixed',
          });
        } else {
          setMonthStyles({
            top: 'auto',
            position: 'static',
          });
        }
      } else {
        setMonthStyles({
          top: 'auto',
          position: 'fixed',
        });
      }
    };
    window.addEventListener('scroll', onScrollHandler);
    return () => removeEventListener('scroll', onScrollHandler);
  }, [innerWidth, pageContent?.scrollTop, sidebarAndContentContainer]);

  const monthTitleMemo = useMemo(() => {
    return <span className="item">{currentMonth.title}</span>;
  }, [currentMonth]);

  const employeesListWidth = useMemo(() => {
    let width = 0;

    if ((selectedEmployees || [])?.length > 0) {
      if (innerWidth <= 755) {
        width = 55;
      } else {
        width = 235;
      }
    } else {
      if (innerWidth <= 755) {
        width = 100;
      } else {
        width = 235;
      }
    }

    return width;
  }, [innerWidth, selectedEmployees]);

  const scheduleWidth = useMemo(
    () => SCHEDULE_ELEMENT_WIDTH * cellsPerRowCount,
    [cellsPerRowCount]
  );

  const stickyContainerWidth = useMemo(
    () => employeesListWidth + scheduleWidth,
    [employeesListWidth, scheduleWidth]
  );

  const onPreviousMonth = useCallback(() => {
    const currentMonthIndex = renderedMonths.findIndex(
      (month) => month === currentMonth.startDate
    );
    if (currentMonthIndex <= 1) {
      moveUserToNewMonth(
        [...new Set([getPreviousMonth(renderedMonths[0]), ...renderedMonths])],
        'backward',
        false
      );
    } else {
      moveUserToNewMonth([...new Set([...renderedMonths])], 'backward', false);
    }
  }, [currentMonth, renderedMonths, moveUserToNewMonth]);

  const onNextMonth = useCallback(() => {
    const currentMonthIndex = renderedMonths.findIndex(
      (month) => month === currentMonth.startDate
    );

    const startDate =
      currentMonthIndex >= renderedMonths.length - 2
        ? renderedMonths[renderedMonths.length - 1]
        : currentMonth.startDate;
    moveUserToNewMonth(
      [...new Set([...renderedMonths, getFollowingMonth(startDate)])],
      'forward',
      false,
      initialSchedules
    );
  }, [currentMonth, renderedMonths, moveUserToNewMonth, initialSchedules]);

  return (
    <div
      className="records table"
      ref={schedulesContainerRef}
      onScroll={handleScroll}
    >
      <div
        className="employees-sticky-container"
        style={{
          width: stickyContainerWidth,
          height: isPageScroll
            ? virtualItems.length * SCHEDULE_ROW_HEIGHT
            : selectedEmployees
            ? selectedEmployees?.length * SCHEDULE_ROW_HEIGHT
            : 0,
        }}
      >
        <div
          className="months-and-days"
          style={{
            marginLeft: employeesListWidth,
            width: scheduleWidth,
            minWidth: scheduleWidth,
          }}
        >
          <div className="background-styles">
            <div className="days" data-testid="dates-and-weekdays">
              {getDatesForFewMonths(renderedMonths).map(
                (date: string, index: number) => {
                  const isFirstMonthDay = date.split('-')[2] === '01';
                  const isFirstRenderedMonth = Math.round(index / 30) === 0;

                  return (
                    <div
                      key={generateKey('WorkSchedule', 'day', date, index)}
                      className={cn('day', `date-${date}`, {
                        weekend: isWeekend(getDayName(date)),
                      })}
                    >
                      {isFirstMonthDay && isFirstRenderedMonth && (
                        <div className="month" style={monthStyles}>
                          <Space>
                            <Button
                              icon={<LeftOutlined />}
                              size="small"
                              data-testid="pre-month"
                              onClick={onPreviousMonth}
                            />
                            {generateSpanWithDataTestId(
                              monthTitleMemo,
                              'active-month'
                            )}
                            <Button
                              icon={<RightOutlined />}
                              size="small"
                              data-testid="next-month"
                              onClick={onNextMonth}
                            />
                          </Space>
                        </div>
                      )}
                      <span className="number" data-testid="date">
                        {Number(date.split('-')[2])}
                      </span>
                      <span className="name" data-testid="weekday">
                        {getDayName(date)}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
          }}
        >
          <div
            className="employees-list-fixing"
            style={{
              width: employeesListWidth,
              height: scrollElementHeight - MONTH_DAYS_ROW_HEIGHT,
            }}
          >
            {selectedEmployees && (
              <>
                <List
                  itemLayout="horizontal"
                  dataSource={virtualItems.map((virtualItem) => {
                    const item = selectedEmployees[virtualItem.index];
                    const {
                      Id,
                      NameFirst,
                      NameLast,
                      Position,
                    }: NEmployees.ModelEmployee = item;
                    return {
                      id: Id,
                      curtailedFullName: generateSpanWithDataTestId(
                        shortName(`${NameLast} ${NameFirst}`, false, 17),
                        'employee-name'
                      ),
                      curtailedPosition: generateSpanWithDataTestId(
                        shortName(Position || '', false, 17),
                        'employee-position'
                      ),
                      fullName: `${NameLast} ${NameFirst}`,
                      position: Position || '',
                      offset: virtualItem.offsetTop,
                    };
                  })}
                  renderItem={(item: NWorkSchedule.EmployeeContainerData) => (
                    <EmployeeContainer
                      item={item}
                      photosAndMiddleNames={photosAndMiddleNames}
                      breakpoint={755}
                      style={{
                        position: 'absolute',
                        top: 0,
                        transform: `translateY(${item.offset}px)`,
                        background: 'white',
                      }}
                    />
                  )}
                />
              </>
            )}
          </div>
          <div
            className="schedule-rows"
            style={{
              width: scheduleWidth,
              height: scrollElementHeight - MONTH_DAYS_ROW_HEIGHT,
            }}
          >
            <div className="cells-container">
              {selectedEmployees &&
                virtualItems.map((virtualItem) => {
                  const employee = selectedEmployees[virtualItem.index];

                  const isFilteredByPosition = selectedPositions?.find(
                    ({ value }) => {
                      if (value === employee.PositionId) {
                        return true;
                      }
                    }
                  );
                  if (
                    (selectedPositions || [])?.length === 0 ||
                    isFilteredByPosition
                  ) {
                    return (
                      <div
                        key={employee.Id}
                        className="records row"
                        data-testid="work-schedule-row"
                        style={{
                          position: 'absolute',
                          top: 0,
                          transform: `translateY(${virtualItem.offsetTop}px)`,
                        }}
                      >
                        {Array.from(cells.get(employee.Id)?.keys() || []).map(
                          (date) => {
                            const cell = cells?.get(employee.Id)?.get(date);
                            const changingCell = changingCells
                              ?.get(employee.Id)
                              ?.get(date);
                            const deletingCell = deletingCells
                              ?.get(employee.Id)
                              ?.get(date);

                            const props = {
                              cell,
                              changingCell,
                              deletingCell,
                              employee,
                              date,
                              changeFewSchedulesCB,
                              hoveredCells,
                              mode,
                              hoverSchedules,
                              rights,
                            };

                            return (
                              cell && (
                                <ScheduleCell
                                  key={`${employee.Id}-${date}`}
                                  {...props}
                                />
                              )
                            );
                          }
                        )}
                      </div>
                    );
                  }
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
